import { useEffect } from "react";
import { usePresence } from "./usePresence";
import { supabase } from "@/integrations/supabase/client";

/**
 * Global presence tracking hook that monitors user activity across the app
 * Updates last_seen_at timestamp and broadcasts presence to all connected clients
 */
export function useGlobalPresence(userId?: string) {
  const { onlineUsers, channel } = usePresence("global_presence", userId);

  useEffect(() => {
    if (!userId) return;

    // Update last_seen_at on mount
    const updateLastSeen = async () => {
      await supabase
        .from('profiles')
        .update({ last_seen_at: new Date().toISOString() })
        .eq('id', userId);
    };

    updateLastSeen();

    // Heartbeat to keep presence fresh. Live online/offline state comes from
    // the realtime presence channel, so this row write only needs to be coarse
    // enough for "last seen" reporting — every 5 minutes, and only while the
    // tab is actually visible (a backgrounded tab was writing a row a minute).
    const HEARTBEAT_MS = 5 * 60 * 1000;
    const heartbeat = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      updateLastSeen();
    };
    const interval = setInterval(heartbeat, HEARTBEAT_MS);

    return () => clearInterval(interval);
  }, [userId]);

  return { onlineUsers, channel };
}
